import { Alert, Box, Stack } from "@mui/material";
import { addHours, startOfToday } from "date-fns";
import { useReducer, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import adminApi from "../../../api/adminApi";
import type { BackupMasterConfiguration } from "../../../api/types/integrationsTypes";
import useClientContext from "../../../context/useClientContext";
import useUserContext from "../../../context/useUserContext";
import DataBackupIcon from "../../common/domain-icons/DataBackupIcon";
import IntegrationPageContainer from "../integration/IntegrationPageContainer";
import DataBackupSetup from "./DataBackupSetup";
import DataBackupSyncActions from "./DataBackupSyncActions";
import DataBackupTabs from "./DataBackupTabs";
import { getInitialState, reducer } from "./dataBackupPageState";
import BackupNowDialog from "./dialogs/BackupNowDialog";

const makeTestConnection = withErrorHandling(adminApi.makeDataBackupTestConnection);
const toggleDailySync = withErrorHandling(adminApi.setDataBackupDailySync);
const updateConfiguration = withErrorHandling(adminApi.setDataBackupConfiguration);

const buildConfiguration = (configuration: BackupMasterConfiguration): BackupMasterConfiguration => {
  return {
    ...configuration,
    hour: addHours(startOfToday(), configuration.hour || 0).getUTCHours(),
  };
};

export const DataBackupPage = () => {
  usePageTitle("Data Backup");

  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { refreshUserData } = useUserContext();
  const { dataBackupApp, hasPermissions } = useClientContext();
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const [showBackupDialog, setShowBackupDialog] = useState(false);

  const canManageDataBackup = hasPermissions(["ManageClientIntegrations"]);

  const [connectionStateResult, connectionStateFetchError] = useFetch(adminApi.getDataBackupConnectionState, {
    onSetData: (data) => dispatch({ type: "SetConnectionState", payload: { ...data } }),
  });

  const [configurationResult, configurationFetchError] = useFetch(adminApi.getDataBackupConfiguration, {
    onSetData: (data) => dispatch({ type: "SetConfiguration", payload: { ...data } }),
  });

  const [configurationOptionsResult, configurationOptionsFetchError] = useFetch(
    adminApi.getDataBackupConfigurationOptions,
    {
      onSetData: (data) => dispatch({ type: "SetConfigurationOptions", payload: data.awsRegionEndpoints }),
    }
  );

  const handleTestConnection = async () => {
    dispatch({ type: "SetActiveProcess", payload: "testingConnection" });
    const [testConnectionResponse, error] = await makeTestConnection();
    if (!error && testConnectionResponse.success) {
      dispatch({
        type: "SetAlert",
        payload: {
          severity: "info",
          message: "The test connection was successful. You can view all logs in the Logs tab.",
        },
      });
    } else {
      dispatch({
        type: "SetAlert",
        payload: {
          severity: "error",
          message: "An error occurred while testing the connection. You can view all logs in the Logs tab.",
        },
      });
    }
    dispatch({ type: "ResetActiveProcess" });
  };

  const handleToggleDailyBackup = async () => {
    dispatch({ type: "SetActiveProcess", payload: "togglingDailySync" });
    const value = !state.enabled;
    const [toggleConnectionResponse, error] = await toggleDailySync({ enabled: value });
    if (!error && value === toggleConnectionResponse.enabled) {
      dispatch({ type: "SetConnectionState", payload: { ...toggleConnectionResponse } });
      sendNotification(`The daily backup was successfully ${value ? "enabled" : "disabled"}.`);
      refreshUserData();
    } else {
      sendNotificationError(`An error occurred while ${value ? "enabling" : "disabling"} the daily backup.`);
    }
    dispatch({ type: "ResetActiveProcess" });
  };

  const handleSaveConfiguration = async () => {
    if (!state.configuration) return;
    dispatch({ type: "SetActiveProcess", payload: "updatingConfiguration" });
    const [, error] = await updateConfiguration(buildConfiguration(state.configuration));
    if (!error) {
      sendNotification("The configuration was saved successfully.");
      dispatch({ type: "ResetConfigurationDirty" });
    } else {
      sendNotificationError("An error occurred while saving the configuration.");
    }
    dispatch({ type: "ResetActiveProcess" });
  };

  if (connectionStateFetchError || configurationFetchError || configurationOptionsFetchError) {
    return <DataLoadingFailed title="Could not load backup data" />;
  }

  if (!connectionStateResult || !configurationResult || !configurationOptionsResult) {
    return <InlineLoader />;
  }

  return (
    <>
      <IntegrationPageContainer
        title="Data Backup"
        Icon={DataBackupIcon}
        overviewText="Configure daily backups of your financial data to AWS S3 for added risk mitigation and regulatory compliance."
        appStatus={dataBackupApp?.status}
        maxWidth="lg"
      >
        <Box sx={{ position: "relative" }}>
          <Stack spacing={3}>
            <DataBackupSetup
              lastChangedBy={state?.lastChangedBy}
              lastChangedOn={state?.lastChangedOn}
              lastBackupDate={state?.lastBackupDate}
              isLatestBackupSuccessful={state?.isLatestBackupSuccessful}
            />
            <DataBackupSyncActions
              dailyBackupEnabled={state.enabled}
              onBackupNow={() => setShowBackupDialog(true)}
              onToggleDailyBackup={handleToggleDailyBackup}
              onTestConnection={handleTestConnection}
              testConnectionProcessing={state.activeProcess === "testingConnection"}
              toggleDailyBackupProcessing={state.activeProcess === "togglingDailySync"}
              readonly={!canManageDataBackup}
              disabled={state.isConfigurationDirty}
            />
            {state.alertMessage && (
              <Alert severity={state.alertMessage.severity} onClose={() => dispatch({ type: "SetAlert" })}>
                {state.alertMessage.message}
              </Alert>
            )}
            {state.configuration && state.configurationOptions && (
              <DataBackupTabs
                state={state}
                onConfigurationSave={handleSaveConfiguration}
                onConfigurationChange={(data) => dispatch({ type: "UpdateConfiguration", payload: data })}
                readonly={!canManageDataBackup}
              />
            )}
          </Stack>
          {state.activeProcess && <InlineLoader overlay sx={{ pointerEvents: "auto", top: 0, left: 0, zIndex: 2 }} />}
        </Box>
      </IntegrationPageContainer>
      {showBackupDialog && (
        <BackupNowDialog
          onCancel={() => setShowBackupDialog(false)}
          onBackupStarted={() => {
            dispatch({
              type: "SetAlert",
              payload: { severity: "info", message: "Backup started successfully. Track progress in the Logs tab." },
            });
            setShowBackupDialog(false);
          }}
          onOperationInProgress={() => {
            dispatch({
              type: "SetAlert",
              payload: {
                severity: "error",
                message:
                  "Unable to start the full data backup operation because another operation is currently in progress.",
              },
            });
            setShowBackupDialog(false);
          }}
          onConnectionFailed={(msg) => {
            dispatch({ type: "SetAlert", payload: { severity: "error", message: msg } });
            setShowBackupDialog(false);
          }}
        />
      )}
    </>
  );
};
